import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { sdkUtils } from '@koala/sdk';
import {
  StyledAccountDropdownContent,
  StyledAccountDropdownNumber,
  StyledDropdownButton,
  StyledDropdownHeaderContent,
  StyledDropdownModalContainer,
  StyledDropdownOverlay,
  StyledIconWrapper,
  StyledLoyaltySkeleton,
  StyledLoyaltySummary,
  StyledLoyaltyPointsContent,
  StyledLoyaltyAccessorContainer,
  StyledPointsSubtext,
  StyledPointsText,
  StyledProgressBar,
  StyledProgressBarFill,
  StyledRewardsBadge,
  StyledRowDivider,
  StyledRowLabel,
  StyledSignOutButton,
} from './styles';
import Icon from '@/components/uielements/icon';
import LoyaltyAccessor from '@/components/account/loyaltyAccessor';
import StringAccessor from '@/components/cmsConfig/stringAccessor';
import { ConnectedXButton } from '@/components/uielements/X';
import { CSS_CLASSES } from '@/constants/cssClassNames';
import { LOYALTY_ACCESSOR_TYPES, LOYALTY_FEATURES } from '@/constants/loyalty';
import { LOYALTY_ROUTES, ROUTES } from '@/constants/routes';
import { useSelector } from '@/redux';
import authActions from '@/redux/auth/actions';
import meActions from '@/redux/me/actions';
import { getAvailableRedeemablesCount } from '@/utils/loyalty';
import { safelyGetString } from '@/utils/stringHelpers';

function RewardsBadgeIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
      <path
        d="M13.5 6C13.5 8.48528 11.4853 10.5 9 10.5C6.51472 10.5 4.5 8.48528 4.5 6C4.5 3.51472 6.51472 1.5 9 1.5C11.4853 1.5 13.5 3.51472 13.5 6Z"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
        stroke="currentColor"
      />
      <path
        d="M11.6077 9.66406L12.75 16.4966L9 14.2466L5.25 16.4966L6.39225 9.66406"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
        stroke="currentColor"
      />
    </svg>
  );
}

function LoyaltyPointsSummary() {
  const loyaltyState = useSelector((state) => state.app.me.loyaltyState);
  const loyaltyStateLoading = useSelector((state) => state.app.me.loyaltyStateLoading);
  const loyaltyStateFailed = useSelector((state) => state.app.me.loyaltyStateFailed);
  const redeemables = useSelector((state) => state.app.me.redeemables);
  const organization = useSelector((state) => state.app.organization);
  const strings = useSelector((state) => state.app.cmsConfig.strings);

  // Loading: show skeleton while loyalty state is being fetched
  if (loyaltyStateLoading && !loyaltyState) {
    return <StyledLoyaltySkeleton data-testid="loyalty-points-skeleton" />;
  }

  // Failed: hide the block entirely
  if (loyaltyStateFailed) {
    return null;
  }

  // No loyalty state data available
  if (!loyaltyState) {
    return null;
  }

  const currentPoints = loyaltyState.points ?? 0;
  const programType = organization?.loyalty?.program_type;

  // Compute next reward target — spread to avoid in-place sort mutating Redux state
  const nextPointTier = sdkUtils.nextLargestRewardPointValue(programType, loyaltyState, [
    ...redeemables,
  ]);

  const pointsUntilNext = sdkUtils.pointsUntilNextReward(currentPoints, nextPointTier);

  // Guard division-by-zero: only compute bar width when there is a target
  const progressBarWidth =
    nextPointTier > 0 ? sdkUtils.calculateProgressBarWidth(currentPoints, nextPointTier) : '0%';

  const pointsLabel = `${currentPoints.toLocaleString()} points`;

  // No rewards configured (empty redeemables, SDK returns 0)
  if (nextPointTier === 0) {
    return (
      <StyledLoyaltySummary>
        <StyledRewardsBadge>
          <RewardsBadgeIcon />
        </StyledRewardsBadge>
        <StyledLoyaltyPointsContent>
          <StyledPointsText>{pointsLabel}</StyledPointsText>
        </StyledLoyaltyPointsContent>
      </StyledLoyaltySummary>
    );
  }

  // At or above last reward threshold — all rewards unlocked
  if (pointsUntilNext === 0 && currentPoints > 0) {
    return (
      <StyledLoyaltySummary>
        <StyledRewardsBadge>
          <RewardsBadgeIcon />
        </StyledRewardsBadge>
        <StyledLoyaltyPointsContent>
          <StyledPointsText>{pointsLabel}</StyledPointsText>
          <StyledPointsSubtext>You can redeem any reward</StyledPointsSubtext>
        </StyledLoyaltyPointsContent>
      </StyledLoyaltySummary>
    );
  }

  // Normal case (includes zero points): show points, "n points to next reward", and progress bar
  const pointsToNextText = safelyGetString(strings, 'rewards.account_menu_points_to_next', {
    number: pointsUntilNext.toLocaleString(),
  });
  const fallbackText = `${pointsUntilNext.toLocaleString()} points to next reward`;

  return (
    <StyledLoyaltySummary>
      <StyledRewardsBadge>
        <RewardsBadgeIcon />
      </StyledRewardsBadge>
      <StyledLoyaltyPointsContent>
        <StyledPointsText>{pointsLabel}</StyledPointsText>
        <StyledPointsSubtext>{pointsToNextText || fallbackText}</StyledPointsSubtext>
        <StyledProgressBar>
          <StyledProgressBarFill
            $width={progressBarWidth}
            role="progressbar"
            aria-valuemin={0}
            aria-valuenow={currentPoints}
            aria-valuemax={nextPointTier}
            aria-label="Progress to next reward"
          />
        </StyledProgressBar>
      </StyledLoyaltyPointsContent>
    </StyledLoyaltySummary>
  );
}

export function AccountDropdown() {
  const dispatch = useDispatch();
  const router = useRouter();
  const me = useSelector((state) => state.app.me.data);
  const loyaltyState = useSelector((state) => state.app.me.loyaltyState);
  const myRedeemables = useSelector((state) => state.app.me.redeemables);
  const accountDropdownOpen = useSelector((state) => state.app.me.accountDropdownOpen);

  const navigationRef = useRef<HTMLUListElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [listCount, setListCount] = useState(0);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyboardNavigation);
    document.addEventListener('mousedown', handleClick, false);

    return () => {
      document.removeEventListener('keydown', handleKeyboardNavigation);
      document.removeEventListener('mousedown', handleClick, false);
    };
  }, []);

  useEffect(() => {
    if (!me) {
      // If user signs out from dropdown, hide the dropdown
      dispatch(meActions.toggleAccountDropdown(false));
    }

    if (accountDropdownOpen) {
      setupListNavigation();
    }

    if (navigationRef.current && activeIndex) {
      const navItems = navigationRef.current.querySelectorAll<HTMLLIElement>(
        'li:not([role="separator"])',
      );
      const targetItem = navItems[activeIndex - 1];
      const linkToFocus = targetItem?.querySelector<HTMLAnchorElement>('a');

      if (linkToFocus) {
        linkToFocus.focus();
      }
    }
  }, [me, accountDropdownOpen, navigationRef.current, activeIndex]);

  // The KeyboardEvent option in Javascript allows us to use an abstracted
  // list of keyboard events that are mapped from various types of keyboards.
  // This list is human readable ("Escape", "ArrowDown", etc)
  const handleKeyboardNavigation = (evt: { key: string }) => {
    // Close modal on 'esc' press
    if (accountDropdownOpen) {
      // space key is mapped as " "
      if (evt.key === 'Escape' || evt.key === ' ') {
        dispatch(meActions.toggleAccountDropdown(false));
      }

      if (evt.key === 'ArrowDown' || evt.key === 'ArrowRight') {
        setActiveIndex(activeIndex < listCount ? activeIndex + 1 : 1);
      }

      if (evt.key === 'ArrowUp' || evt.key === 'ArrowLeft') {
        setActiveIndex(activeIndex > 1 ? activeIndex - 1 : listCount);
      }

      if (evt.key === 'Home') {
        setActiveIndex(1);
      }

      if (evt.key === 'End') {
        setActiveIndex(listCount);
      }
    }
  };

  /** @TODO improve event typing. */
  // @ts-expect-error
  const handleClick = (e) => {
    if (e.target.id !== 'Loyalty-Dropdown-Overlay') {
      return;
    }
    dispatch(meActions.toggleAccountDropdown(false));
  };

  const setupListNavigation = () => {
    if (navigationRef.current) {
      setActiveIndex(1);
      setListCount(navigationRef.current.querySelectorAll('li:not([role="separator"])').length);

      // we want to try and focus the first element
      setTimeout(() => {
        const linkToFocus = navigationRef?.current?.querySelector<HTMLAnchorElement>(
          'li:not([role="separator"]) a',
        );

        if (linkToFocus) {
          linkToFocus.focus();
        }
      }, 100);
    }
  };

  const closeDropdown = () => dispatch(meActions.toggleAccountDropdown(false));

  // Rewards only — offers are not counted (AC #7 / TEAM-15659).
  const currentPoints = loyaltyState?.points ?? 0;
  const rewardsCount = getAvailableRedeemablesCount(myRedeemables, currentPoints);

  const cancelRedirect = router?.pathname.includes(ROUTES.ACCOUNT);

  return (
    <StyledDropdownOverlay $isVisible={accountDropdownOpen} id="Loyalty-Dropdown-Overlay">
      <StyledDropdownModalContainer
        aria-label="Account and Loyalty"
        $isVisible={accountDropdownOpen}
      >
        <ConnectedXButton
          onClick={closeDropdown}
          id="loyalty-dropdown-x-button"
          name="Close dropdown"
        />
        <StyledAccountDropdownContent>
          <StyledDropdownHeaderContent>
            <h3 id="account-menu-label">Hi, {me?.first_name}!</h3>

            <StyledLoyaltyAccessorContainer>
              <LoyaltyAccessor
                checkType={LOYALTY_ACCESSOR_TYPES.FEATURE}
                checkName={LOYALTY_FEATURES.SHOW_LOYALTY_STATE}
                component={
                  <>
                    {loyaltyState?.membership_level_name && (
                      <p>{loyaltyState?.membership_level_name}</p>
                    )}
                    <LoyaltyPointsSummary />
                  </>
                }
              />
            </StyledLoyaltyAccessorContainer>
          </StyledDropdownHeaderContent>
          <ul ref={navigationRef}>
            {/* Rewards */}
            <LoyaltyAccessor
              checkType={LOYALTY_ACCESSOR_TYPES.ROUTE}
              checkName={LOYALTY_ROUTES.REWARDS}
              component={
                <li>
                  <Link href={LOYALTY_ROUTES.REWARDS} passHref={true} legacyBehavior>
                    <StyledDropdownButton
                      aria-label="Go to Rewards page"
                      className={CSS_CLASSES.LOYALTY.DROPDOWN_BUTTON}
                      onClick={closeDropdown}
                    >
                      <StyledIconWrapper>
                        <Icon.AccountRewards title="Rewards" />
                      </StyledIconWrapper>
                      <StyledRowLabel>
                        <StringAccessor accessor="rewards.title" />
                      </StyledRowLabel>
                      {rewardsCount > 0 && (
                        <StyledAccountDropdownNumber>{rewardsCount}</StyledAccountDropdownNumber>
                      )}
                    </StyledDropdownButton>
                  </Link>
                </li>
              }
            />
            {/* Orders (renamed from Order History — TEAM-15659) */}
            <LoyaltyAccessor
              checkType={LOYALTY_ACCESSOR_TYPES.ROUTE}
              checkName={LOYALTY_ROUTES.ORDER_HISTORY}
              component={
                <li>
                  <Link href={LOYALTY_ROUTES.ORDER_HISTORY} passHref={true} legacyBehavior>
                    <StyledDropdownButton
                      aria-label="Go to Order History page"
                      className={CSS_CLASSES.LOYALTY.DROPDOWN_BUTTON}
                      onClick={closeDropdown}
                    >
                      <StyledIconWrapper>
                        <Icon.AccountOrders title="Orders" />
                      </StyledIconWrapper>
                      <StyledRowLabel>Orders</StyledRowLabel>
                    </StyledDropdownButton>
                  </Link>
                </li>
              }
            />
            {/* Favorites */}
            <LoyaltyAccessor
              checkType={LOYALTY_ACCESSOR_TYPES.ROUTE}
              checkName={LOYALTY_ROUTES.FAVORITES}
              component={
                <li>
                  <Link href={LOYALTY_ROUTES.FAVORITES} passHref={true} legacyBehavior>
                    <StyledDropdownButton
                      aria-label="Go to Favorites page"
                      className={CSS_CLASSES.LOYALTY.DROPDOWN_BUTTON}
                      onClick={closeDropdown}
                    >
                      <StyledIconWrapper>
                        <Icon.AccountFavorites title="Favorites" />
                      </StyledIconWrapper>
                      <StyledRowLabel>
                        <StringAccessor accessor="favorites.title" />
                      </StyledRowLabel>
                    </StyledDropdownButton>
                  </Link>
                </li>
              }
            />
            {/* Account */}
            <LoyaltyAccessor
              checkType={LOYALTY_ACCESSOR_TYPES.ROUTE}
              checkName={LOYALTY_ROUTES.ACCOUNT}
              component={
                <li>
                  <Link href={LOYALTY_ROUTES.ACCOUNT} passHref={true} legacyBehavior>
                    <StyledDropdownButton
                      aria-label="Go to Account page"
                      className={CSS_CLASSES.LOYALTY.DROPDOWN_BUTTON}
                      onClick={closeDropdown}
                    >
                      <StyledIconWrapper>
                        <Icon.AccountProfile title="Account" />
                      </StyledIconWrapper>
                      <StyledRowLabel>
                        <StringAccessor accessor="account.title" />
                      </StyledRowLabel>
                    </StyledDropdownButton>
                  </Link>
                </li>
              }
            />
            {/* Divider */}
            <StyledRowDivider role="separator" aria-hidden="true" />
            {/* Sign out */}
            <li>
              <StyledSignOutButton
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  closeDropdown();
                  dispatch(authActions.logout(cancelRedirect));
                }}
                aria-label="Click to Sign Out"
                className={CSS_CLASSES.LOYALTY.DROPDOWN_BUTTON}
              >
                <StyledIconWrapper>
                  <Icon.AccountSignOut title="Sign out" />
                </StyledIconWrapper>
                <StyledRowLabel>
                  <StringAccessor accessor="account.sign_out_button" />
                </StyledRowLabel>
              </StyledSignOutButton>
            </li>
          </ul>
        </StyledAccountDropdownContent>
      </StyledDropdownModalContainer>
    </StyledDropdownOverlay>
  );
}
